import { motion } from "framer-motion";
import { AlertCircle } from "lucide-react";
import { useForm } from "react-hook-form";

import { Card } from "@/components/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import UploadInput from "@/features/file-upload/components/upload-input";
import { queryClient } from "@/lib/react-query";
import { HttpError, isBadRequest } from "@/types/error";
import { zodResolver } from "@hookform/resolvers/zod";

import { useUpdateUser } from "../api/update-user";
import {
  UserAvatarFormData,
  userAvatarSchema,
} from "../types/user-avatar.schema";

type UserAvatarFormProps = {
  userId: string;
  avatarURL: string | null;
};

function apiDataToFormData(data: UserAvatarFormProps): UserAvatarFormData {
  return {
    avatarUrl: data.avatarURL ?? undefined,
  };
}

export function UserAvatarForm(props: UserAvatarFormProps) {
  const { userId } = props;
  const form = useForm<UserAvatarFormData>({
    resolver: zodResolver(userAvatarSchema),
    defaultValues: apiDataToFormData(props),
  });

  const { mutateAsync: updateUser } = useUpdateUser({
    onSuccess: (result) => {
      form.reset({ avatarUrl: result.avatarURL ?? undefined });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  async function handleUpdateUserInformation(data: UserAvatarFormData) {
    try {
      await updateUser({
        userId: userId,
        user: {
          avatarURL: data.avatarUrl ?? undefined,
        },
      });
    } catch (error) {
      const httpError = error as HttpError;
      if (isBadRequest(httpError)) {
        form.setError("root", { message: httpError.message.join(",") });
      } else {
        console.error("Unexpected error:", error);
        form.setError("root", {
          message: "An unexpected error occurred. Please try again.",
        });
      }
    }
  }

  return (
    <Form {...form}>
      <Card>
        <form onSubmit={form.handleSubmit(handleUpdateUserInformation)}>
          <Card.Header
            title="Profile Media"
            description="Banner & Profile Image"
          />
          <Card.Content className="space-y-4">
            <FormField
              control={form.control}
              name="avatarUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Avatar</FormLabel>
                  <FormDescription>
                    You can upload .jpeg, .png, .gif, or .webp files.
                  </FormDescription>
                  <FormControl>
                    <UploadInput
                      name="avatarUrl"
                      onChange={field.onChange}
                      value={field.value}
                      allowedFileTypes={[
                        "image/jpeg",
                        "image/png",
                        "image/gif",
                        "image/webp",
                      ]}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {form.formState.errors.root && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unexpected error</AlertTitle>
                <AlertDescription>
                  {form.formState.errors.root.message}
                </AlertDescription>
              </Alert>
            )}
          </Card.Content>
          <motion.div
            className="overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={
              form.formState.isDirty
                ? { opacity: 1, height: "auto" } // 224px is equivalent to h-56
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <Card.Footer className="flex gap-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset({ avatarUrl: undefined })}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={form.formState.isSubmitting}
                isAnimated
              >
                Update
              </Button>
            </Card.Footer>
          </motion.div>
        </form>
      </Card>
    </Form>
  );
}
